/**
 * GalleryReviewPage - Dedicated split-pane image review interface.
 *
 * The primary workflow page for reviewing and approving job images.
 * Features:
 * - Split-pane layout (25% thumbnails / 75% preview)
 * - Auto-select first pending image with animated highlight
 * - Keyboard shortcuts (A/R/F/I/C/arrows)
 * - Bulk selection mode
 * - Progress tracking
 *
 * @see /src/features/gallery/layouts/GalleryLayout.tsx - Parent layout
 * @see /src/features/gallery/context/GalleryDetailContext.tsx - State provider
 */

import { useState, useEffect, useMemo, useCallback, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { CheckSquare, Square, ThumbsUp, ThumbsDown, PartyPopper } from 'lucide-react'
import SplitPane from '@/components/ui/split-pane'

import { useGalleryDetail } from '../context/GalleryDetailContext'
import { useKeyboardShortcuts } from '../hooks/useKeyboardShortcuts'
import { ReviewThumbnailList } from '../components/review/ReviewThumbnailList'
import { ReviewPreviewPanel } from '../components/review/ReviewPreviewPanel'
import type { ReviewFilter } from '../components/review/ReviewFilterPills'
import type { GalleryAllImages } from '../types'

/**
 * Filter images based on current filter selection.
 */
function filterImages(images: GalleryAllImages[], filter: ReviewFilter): GalleryAllImages[] {
  const jobImages = images.filter((img) => img.image_type === 'job_image')
  if (filter === 'all') return jobImages
  return jobImages.filter((img) => img.review_status === filter)
}

export default function GalleryReviewPage() {
  const {
    gallery,
    images,
    jobImages,
    stats,
    isLoading,
    approveImage,
    rejectImage,
    flagImage,
    toggleInclude,
    setCover,
    bulkApprove,
    bulkReject,
    isApproving,
    openLightbox,
  } = useGalleryDetail()

  // Local state
  const [filter, setFilter] = useState<ReviewFilter>('all')
  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [bulkMode, setBulkMode] = useState(false)
  const [bulkSelectedIds, setBulkSelectedIds] = useState<number[]>([])
  const [hasAutoSelected, setHasAutoSelected] = useState(false)

  // Filter images
  const filteredImages = useMemo(() => filterImages(images, filter), [images, filter])

  // Get currently selected image
  const selectedImage = useMemo(
    () => filteredImages.find((img) => img.original_id === selectedId) ?? null,
    [filteredImages, selectedId]
  )

  // Current index in filtered list
  const currentIndex = useMemo(() => {
    if (selectedId === null) return -1
    return filteredImages.findIndex((img) => img.original_id === selectedId)
  }, [filteredImages, selectedId])

  // ─────────────────────────────────────────────────────────────
  // Auto-select first pending image on mount
  // ─────────────────────────────────────────────────────────────

  useEffect(() => {
    if (hasAutoSelected || isLoading || filteredImages.length === 0) return

    // Find first pending image
    const firstPending = jobImages.find((img) => img.review_status === 'pending')

    if (firstPending) {
      // Set filter to pending if there are pending images
      setFilter('pending')
      setSelectedId(firstPending.original_id)
    } else {
      // Otherwise select first image in current filter
      setSelectedId(filteredImages[0]?.original_id ?? null)
    }

    setHasAutoSelected(true)
  }, [hasAutoSelected, isLoading, filteredImages, jobImages])

  // Update selection when filter changes and current selection is filtered out
  useEffect(() => {
    if (selectedId === null) return

    const stillVisible = filteredImages.some((img) => img.original_id === selectedId)
    if (!stillVisible && filteredImages.length > 0) {
      setSelectedId(filteredImages[0].original_id)
    } else if (filteredImages.length === 0) {
      setSelectedId(null)
    }
  }, [filteredImages, selectedId])

  // ─────────────────────────────────────────────────────────────
  // Navigation handlers
  // ─────────────────────────────────────────────────────────────

  // Use ref to store filteredImages for navigation to avoid stale closures
  const filteredImagesRef = useRef(filteredImages)
  filteredImagesRef.current = filteredImages

  // Debounce navigation to prevent dual-selection from key repeat
  const lastNavigationRef = useRef<number>(0)
  const NAVIGATION_DEBOUNCE_MS = 50

  const navigateToNext = useCallback(() => {
    const now = Date.now()
    if (now - lastNavigationRef.current < NAVIGATION_DEBOUNCE_MS) return
    lastNavigationRef.current = now

    const images = filteredImagesRef.current
    if (images.length === 0) return

    // Use functional update to get latest selectedId
    setSelectedId((prevId) => {
      const currentIdx = prevId === null ? -1 : images.findIndex((img) => img.original_id === prevId)
      const nextIdx = (currentIdx + 1) % images.length
      return images[nextIdx]?.original_id ?? prevId
    })
  }, [])

  const navigateToPrev = useCallback(() => {
    const now = Date.now()
    if (now - lastNavigationRef.current < NAVIGATION_DEBOUNCE_MS) return
    lastNavigationRef.current = now

    const images = filteredImagesRef.current
    if (images.length === 0) return

    // Use functional update to get latest selectedId
    setSelectedId((prevId) => {
      const currentIdx = prevId === null ? 0 : images.findIndex((img) => img.original_id === prevId)
      const prevIdx = currentIdx <= 0 ? images.length - 1 : currentIdx - 1
      return images[prevIdx]?.original_id ?? prevId
    })
  }, [])

  // ─────────────────────────────────────────────────────────────
  // Action handlers
  // ─────────────────────────────────────────────────────────────

  const handleApprove = useCallback(() => {
    if (!selectedId) return
    approveImage(selectedId)
    // Auto-advance to next pending
    const nextPending = filteredImages.find(
      (img, idx) => idx > currentIndex && img.review_status === 'pending'
    )
    if (nextPending) {
      setSelectedId(nextPending.original_id)
    } else {
      navigateToNext()
    }
  }, [selectedId, approveImage, filteredImages, currentIndex, navigateToNext])

  const handleReject = useCallback(() => {
    if (!selectedId) return
    rejectImage(selectedId)
    navigateToNext()
  }, [selectedId, rejectImage, navigateToNext])

  const handleFlag = useCallback(() => {
    if (!selectedId) return
    flagImage(selectedId)
    navigateToNext()
  }, [selectedId, flagImage, navigateToNext])

  const handleToggleInclude = useCallback(() => {
    if (!selectedId || !selectedImage) return
    // Determine if image is included based on is_cover or sort_order (is_included doesn't exist on view)
    const isIncluded = selectedImage.is_cover ?? selectedImage.sort_order !== null
    toggleInclude(selectedId, isIncluded)
  }, [selectedId, selectedImage, toggleInclude])

  const handleSetCover = useCallback(() => {
    if (!selectedImage?.image_url) return
    // Pass original_id (which is gallery_job_images.id for job images) to properly set is_cover flag
    setCover(selectedImage.image_url, selectedImage.original_id ?? undefined)
  }, [selectedImage, setCover])

  const handleOpenLightbox = useCallback(() => {
    if (currentIndex === -1) return
    openLightbox(currentIndex, filteredImages)
  }, [currentIndex, filteredImages, openLightbox])

  // ─────────────────────────────────────────────────────────────
  // Bulk mode handlers
  // ─────────────────────────────────────────────────────────────

  const handleBulkToggle = useCallback((id: number) => {
    setBulkSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    )
  }, [])

  const handleBulkApprove = useCallback(() => {
    if (bulkSelectedIds.length === 0) return
    bulkApprove(bulkSelectedIds)
    setBulkSelectedIds([])
    setBulkMode(false)
  }, [bulkSelectedIds, bulkApprove])

  const handleBulkReject = useCallback(() => {
    if (bulkSelectedIds.length === 0) return
    bulkReject(bulkSelectedIds)
    setBulkSelectedIds([])
    setBulkMode(false)
  }, [bulkSelectedIds, bulkReject])

  const handleSelectAll = useCallback(() => {
    const allIds = filteredImages
      .map((img) => img.original_id)
      .filter((id): id is number => id !== null)
    setBulkSelectedIds(allIds)
  }, [filteredImages])

  const handleDeselectAll = useCallback(() => {
    setBulkSelectedIds([])
  }, [])

  // ─────────────────────────────────────────────────────────────
  // Keyboard shortcuts
  // ─────────────────────────────────────────────────────────────

  useKeyboardShortcuts({
    onApprove: handleApprove,
    onReject: handleReject,
    onFlag: handleFlag,
    onToggleInclude: handleToggleInclude,
    onSetCover: handleSetCover,
    onNext: navigateToNext,
    onPrev: navigateToPrev,
    enabled: !bulkMode && !isLoading,
  })

  // ─────────────────────────────────────────────────────────────
  // All caught up state
  // ─────────────────────────────────────────────────────────────

  const allReviewed = stats.pending === 0 && stats.jobImages > 0

  if (allReviewed && filter === 'pending') {
    return (
      <div className="h-full flex flex-col items-center justify-center p-8 text-center">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.5, type: 'spring' }}
          className="space-y-4"
        >
          <PartyPopper className="h-16 w-16 text-success mx-auto" />
          <h2 className="text-2xl font-semibold">All caught up!</h2>
          <p className="text-muted-foreground max-w-md">
            All {stats.jobImages} images have been reviewed.
            {stats.approved > 0 && (
              <span className="block mt-1">
                <Badge variant="success" className="mr-1">{stats.approved}</Badge> approved
                {stats.rejected > 0 && (
                  <>
                    {' · '}
                    <Badge variant="destructive" className="mr-1">{stats.rejected}</Badge> rejected
                  </>
                )}
              </span>
            )}
          </p>
          <div className="flex gap-3 justify-center pt-4">
            <Button variant="outline" onClick={() => setFilter('all')}>
              View All Images
            </Button>
            <Button variant="outline" onClick={() => setFilter('approved')}>
              View Approved
            </Button>
          </div>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col">
      {/* Bulk mode toolbar */}
      <AnimatePresence>
        {bulkMode && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden"
          >
            <div className="flex items-center justify-between px-4 py-3 bg-muted border-b">
              <div className="flex items-center gap-4">
                <span className="text-sm font-medium">
                  {bulkSelectedIds.length} selected
                </span>
                <Button variant="ghost" size="sm" onClick={handleSelectAll}>
                  Select All
                </Button>
                <Button variant="ghost" size="sm" onClick={handleDeselectAll}>
                  Clear
                </Button>
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleBulkApprove}
                  disabled={bulkSelectedIds.length === 0}
                  className="bg-success hover:bg-success/90 gap-2"
                >
                  <ThumbsUp className="h-4 w-4" />
                  Approve ({bulkSelectedIds.length})
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={handleBulkReject}
                  disabled={bulkSelectedIds.length === 0}
                  className="gap-2"
                >
                  <ThumbsDown className="h-4 w-4" />
                  Reject ({bulkSelectedIds.length})
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setBulkMode(false)
                    setBulkSelectedIds([])
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main split pane */}
      <div className="flex-1 min-h-0">
        <SplitPane
          left={
            <ReviewThumbnailList
              images={images}
              filter={filter}
              onFilterChange={setFilter}
              selectedId={selectedId}
              onSelect={setSelectedId}
              stats={stats}
              bulkMode={bulkMode}
              bulkSelectedIds={bulkSelectedIds}
              onBulkToggle={handleBulkToggle}
            />
          }
          right={
            <ReviewPreviewPanel
              image={selectedImage}
              coverImageUrl={gallery?.cover_image}
              onApprove={handleApprove}
              onReject={handleReject}
              onFlag={handleFlag}
              onToggleInclude={handleToggleInclude}
              onSetCover={handleSetCover}
              onOpenLightbox={handleOpenLightbox}
              onPrev={navigateToPrev}
              onNext={navigateToNext}
              isLoading={isApproving}
              currentIndex={currentIndex}
              totalImages={filteredImages.length}
              filterLabel={filter}
            />
          }
          initialLeftPercent={25}
          minLeftPercent={15}
          maxLeftPercent={40}
          storageKey="gallery:review:split"
        />
      </div>

      {/* Bottom bar with bulk mode toggle */}
      <div className="shrink-0 px-4 py-2 border-t bg-muted/30 flex items-center justify-between">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setBulkMode(!bulkMode)}
          className="gap-2"
        >
          {bulkMode ? (
            <>
              <CheckSquare className="h-4 w-4" />
              Exit Bulk Mode
            </>
          ) : (
            <>
              <Square className="h-4 w-4" />
              Bulk Select
            </>
          )}
        </Button>
        <div className="text-xs text-muted-foreground">
          {stats.pending > 0 ? (
            <span>
              <span className="font-medium text-foreground">{stats.pending}</span> images pending review
            </span>
          ) : (
            <span className="text-success">All images reviewed</span>
          )}
        </div>
      </div>
    </div>
  )
}
